import { useState, SetStateAction, Dispatch } from 'react';

import { TextField, Box, InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Button } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { RiCalendarLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { TimelineFilters } from '@pages/accountTimeline/AccountTimeline';
import getEnumTranslation from '@utils/getEnumTranslation';

export type TimelineFilterFormProps = {
    setTimelineFilters: Dispatch<SetStateAction<TimelineFilters>>;
    TimelineFilters: TimelineFilters;
    setFilterTimelineOpen: Dispatch<SetStateAction<boolean>>;
    refetch?: () => void;
};

export type TimelineFormData = {
    timelineDateStart?: dayjs.Dayjs | null;
    timelineDateEnd?: dayjs.Dayjs | null;
    timelineType?: string;
    categoryType?: string;
};

const TimelineFilterForm = ({
    setTimelineFilters,
    TimelineFilters,
    setFilterTimelineOpen,
    refetch,
}: TimelineFilterFormProps) => {
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

    const {
        handleSubmit,
        control,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: {
            timelineDateStart:
                TimelineFilters?.timelineDateStart &&
                dayjs(TimelineFilters?.timelineDateStart),
            timelineDateEnd:
                TimelineFilters?.timelineDateEnd &&
                dayjs(TimelineFilters?.timelineDateEnd),
            categoryType: TimelineFilters?.categoryType || '',
            timelineType: TimelineFilters?.timelineType || '',
        },
    });

    const onSubmit = (data: TimelineFormData) => {
        const newData: TimelineFilters = {};
        if (data?.timelineDateStart) {
            newData.timelineDateStart = data.timelineDateStart.toISOString();
        }
        if (data?.timelineDateEnd) {
            newData.timelineDateEnd = data.timelineDateEnd.toISOString();
        }
        newData.timelineType = data.timelineType.toLocaleUpperCase();
        newData.categoryType = data.categoryType.toLocaleUpperCase();
        setTimelineFilters(newData);
        setFilterTimelineOpen(false);
        refetch();
    };

    return (
        <form
            data-testid="timeline-filter-form"
            style={{ width: '100%' }}
            onSubmit={(e) => {
                e.preventDefault();
            }}>
            <PDialogContent>
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-evenly">
                    <Controller
                        name="timelineDateStart"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                open={startDatePickerOpen}
                                disableFuture
                                label={
                                    <FormattedMessage
                                        id="TimelineFilterForm.startDate"
                                        defaultMessage="Start Date"
                                    />
                                }
                                {...field}
                                onClose={() => setStartDatePickerOpen(false)}
                                renderInput={(
                                    params: Record<string, unknown>,
                                ) => (
                                    <TextField
                                        onClick={() =>
                                            setStartDatePickerOpen(true)
                                        }
                                        fullWidth
                                        {...params}
                                        sx={{ mr: 1 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <RiCalendarLine />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                    <Controller
                        name="timelineDateEnd"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                open={endDatePickerOpen}
                                disableFuture
                                label={
                                    <FormattedMessage
                                        id="TimelineFilterForm.endDate"
                                        defaultMessage="End Date"
                                    />
                                }
                                {...field}
                                onClose={() => setEndDatePickerOpen(false)}
                                renderInput={(
                                    params: Record<string, unknown>,
                                ) => (
                                    <TextField
                                        onClick={() =>
                                            setEndDatePickerOpen(true)
                                        }
                                        fullWidth
                                        {...params}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <RiCalendarLine />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                </Box>
                <Controller
                    name="timelineType"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="TimelineFilterForm.timelineType"
                                    defaultMessage="Type"
                                />
                            }
                        />
                    )}
                />
                <Controller
                    name="categoryType"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={
                                <FormattedMessage
                                    id="TimelineFilterForm.categortyType"
                                    defaultMessage="Category"
                                />
                            }
                        />
                    )}
                />
            </PDialogContent>
            <PDialogAction>
                <Button
                    onClick={() => {
                        setTimelineFilters({});
                        setFilterTimelineOpen(false);
                        reset({
                            timelineDateStart: undefined,
                            timelineDateEnd: undefined,
                            categoryType: '',
                            timelineType: '',
                        });
                    }}>
                    {getEnumTranslation('reset')}
                </Button>
                <Button
                    disabled={!isDirty}
                    onClick={handleSubmit(onSubmit)}
                    type="submit"
                    variant="contained">
                    {getEnumTranslation('apply')}
                </Button>
            </PDialogAction>
        </form>
    );
};

export default TimelineFilterForm;
